import React, { useState, useEffect } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    LinearProgress,
    IconButton,
    Tooltip,
    Chip,
    Button,
    useTheme,
    useMediaQuery,
    Stack,
    Divider,
    Avatar,
    Fade,
    Zoom,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Snackbar,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    AttachMoney,
    Receipt,
    Refresh,
    Warning,
    People,
    AccountBalance,
    Assessment,
    Download,
    CalendarToday,
    CheckCircle,
    Cancel,
    Pending,
    CurrencyExchange,
    Inventory,
    Business,
    PhoneAndroid,
    CloudDone,
    WifiOff,
} from '@mui/icons-material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    AreaChart,
    Area,
    ComposedChart,
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff6b6b', '#4ecdc4', '#45b7d1'];
const STATUS_COLORS = {
    'Pending': '#FF9800',
    'Yes': '#4CAF50',
    'No': '#F44336'
};

const CURRENCY_SYMBOLS = {
    'USD': '$',
    'KES': 'KSh',
    'EUR': '€',
    'GBP': '£',
    'CNY': '¥',
    'JPY': '¥',
};

export default function AdminDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [availableCurrencies, setAvailableCurrencies] = useState(['USD', 'KES']);
    const [exchangeRates, setExchangeRates] = useState({});
    const [ratesLoading, setRatesLoading] = useState(false);
    const [ratesError, setRatesError] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const token = localStorage.getItem("token");

    // Fetch exchange rates
    const fetchExchangeRates = async (baseCurrency = 'USD') => {
        setRatesLoading(true);
        setRatesError(false);
        try {
            const response = await axios.get(
                `https://api.exchangerate-api.com/v4/latest/${baseCurrency}`
            );
            setExchangeRates(response.data.rates);
            setRatesError(false);

            let displayRate = 'N/A';
            if (baseCurrency === 'USD' && response.data.rates.KES) {
                displayRate = `${response.data.rates.KES.toFixed(4)} KES`;
            } else if (baseCurrency === 'KES' && response.data.rates.USD) {
                displayRate = `${response.data.rates.USD.toFixed(4)} USD`;
            }

            setSnackbar({
                open: true,
                message: `Exchange rates updated (1 ${baseCurrency} = ${displayRate})`,
                severity: 'success'
            });
            return response.data.rates;
        } catch (error) {
            console.error('Error fetching exchange rates:', error);
            setRatesError(true);
            setSnackbar({
                open: true,
                message: 'Failed to fetch live exchange rates. Using cached rates.',
                severity: 'warning'
            });
            // Use default rates as fallback
            const defaultRates = getDefaultRates();
            setExchangeRates(defaultRates);
            return defaultRates;
        } finally {
            setRatesLoading(false);
        }
    };

    const getDefaultRates = () => {
        return {
            'USD': 1,
            'KES': 150.50,
            'EUR': 0.85,
            'GBP': 0.73,
            'CNY': 6.45,
            'JPY': 110.00,
        };
    };

    const getLiveRate = (fromCurrency, toCurrency) => {
        if (fromCurrency === toCurrency) return 1;

        if (exchangeRates && Object.keys(exchangeRates).length > 0) {
            const fromRate = exchangeRates[fromCurrency];
            const toRate = exchangeRates[toCurrency];

            if (fromRate && toRate) {
                if (fromRate === 1) {
                    return toRate;
                }
                return toRate / fromRate;
            }
        }

        const defaultRates = getDefaultRates();
        const fromDefault = defaultRates[fromCurrency];
        const toDefault = defaultRates[toCurrency];

        if (fromDefault && toDefault) {
            return toDefault / fromDefault;
        }

        return 1;
    };

    const convertAmount = (amount, fromCurrency, toCurrency) => {
        if (!amount) return 0;
        const numAmount = parseFloat(amount);
        if (isNaN(numAmount)) return 0;

        if (fromCurrency === toCurrency) {
            return numAmount;
        }

        const rate = getLiveRate(fromCurrency, toCurrency);
        return numAmount * rate;
    };

    // Fetch data
    useEffect(() => {
        fetchDashboardData();
        fetchExchangeRates('USD');
    }, []);

    // Re-process data when currency changes
    useEffect(() => {
        if (dashboardData && dashboardData.rawData) {
            processDashboardData(dashboardData.rawData, selectedCurrency);
        }
    }, [selectedCurrency]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            // Fetch clients/transactions
            const clientsRes = await axios.get(`${API_URL}/clients`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Fetch expenses
            const expensesRes = await axios.get(`${API_URL}/api/expenses/user/1/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Fetch sync status (if available)
            let syncStatus = { pending: 0, synced: 0, total: 0 };
            try {
                const syncRes = await axios.get(`${API_URL}/sync/sync/status`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                syncStatus = syncRes.data.data || syncStatus;
            } catch (e) {
                console.log('Sync status not available');
            }

            const rawData = {
                clients: clientsRes.data,
                expenses: expensesRes.data.data?.expenses || [],
                syncStatus,
            };

            // Store raw data for reprocessing
            setDashboardData(prev => ({ ...prev, rawData }));

            // Process data
            processDashboardData(rawData, selectedCurrency);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const processDashboardData = (rawData, targetCurrency) => {
        const { clients, expenses, syncStatus } = rawData;

        // Client analytics with currency conversion
        const totalClients = clients.length;

        // Convert all revenue to target currency
        let totalRevenue = 0;
        const clientRevenue = {};
        const currencyBreakdown = {};

        clients.forEach(c => {
            const curr = c.currency || 'KES';
            const amount = parseFloat(c.quotation_amount) || 0;
            const convertedAmount = convertAmount(amount, curr, targetCurrency);

            totalRevenue += convertedAmount;

            // Track per client
            const key = c.customer_id || c.contact_name;
            if (!clientRevenue[key]) {
                clientRevenue[key] = {
                    name: c.contact_name || 'Unknown',
                    company: c.company_name || 'N/A',
                    revenue: 0,
                    count: 0,
                    original_currency: curr,
                };
            }
            clientRevenue[key].revenue += convertedAmount;
            clientRevenue[key].count += 1;

            // Currency breakdown
            if (!currencyBreakdown[curr]) {
                currencyBreakdown[curr] = { total: 0, converted_total: 0, count: 0 };
            }
            currencyBreakdown[curr].total += amount;
            currencyBreakdown[curr].converted_total += convertedAmount;
            currencyBreakdown[curr].count += 1;
        });

        // Top clients by converted revenue
        const topClients = Object.values(clientRevenue)
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 5);

        // Status counts
        const completedCount = clients.filter(c => c.conversion_status === 'Yes').length;
        const pendingCount = clients.filter(c => c.conversion_status === 'Pending').length;
        const failedCount = clients.filter(c => c.conversion_status === 'No').length;

        // Expense analytics with currency conversion
        let totalExpenses = 0;
        expenses.forEach(e => {
            const curr = e.currency || 'KES';
            const amount = parseFloat(e.amount) || 0;
            totalExpenses += convertAmount(amount, curr, targetCurrency);
        });
        const expenseCount = expenses.length;
        const linkedExpenses = expenses.filter(e => e.client_transaction_id).length;

        // Service/Package analytics
        const serviceCount = {};
        clients.forEach(c => {
            let services = c.nature_of_service;
            if (typeof services === 'string') {
                try { services = JSON.parse(services); } catch (e) { services = [services]; }
            }
            if (!Array.isArray(services)) services = [services];
            services.forEach(s => {
                if (s && s.trim()) {
                    serviceCount[s] = (serviceCount[s] || 0) + 1;
                }
            });
        });

        // Package analytics
        const packageCount = {};
        clients.forEach(c => {
            if (c.package_name) {
                packageCount[c.package_name] = (packageCount[c.package_name] || 0) + 1;
            }
        });

        // Monthly trend with converted amounts
        const monthlyTrend = {};
        clients.forEach(c => {
            if (c.booking_date) {
                const month = format(new Date(c.booking_date), 'MMM yyyy');
                const curr = c.currency || 'KES';
                const amount = parseFloat(c.quotation_amount) || 0;
                const convertedAmount = convertAmount(amount, curr, targetCurrency);

                if (!monthlyTrend[month]) {
                    monthlyTrend[month] = { month, revenue: 0, count: 0 };
                }
                monthlyTrend[month].revenue += convertedAmount;
                monthlyTrend[month].count += 1;
            }
        });

        // Profit calculation
        let totalProfit = 0;
        let profitMargin = 0;
        if (totalRevenue > 0 && totalExpenses > 0) {
            totalProfit = totalRevenue - totalExpenses;
            profitMargin = (totalProfit / totalRevenue) * 100;
        }

        const processedData = {
            totalClients,
            totalRevenue,
            totalExpenses,
            totalProfit,
            profitMargin,
            completedCount,
            pendingCount,
            failedCount,
            expenseCount,
            linkedExpenses,
            serviceCount,
            packageCount,
            monthlyTrend: Object.values(monthlyTrend).sort((a, b) => {
                const dateA = new Date(a.month);
                const dateB = new Date(b.month);
                return dateA - dateB;
            }),
            currencyBreakdown,
            topClients,
            syncStatus,
            targetCurrency,
            totalTransactions: clients.length,
            rawData,
        };

        // Extract available currencies
        const currencies = Object.keys(currencyBreakdown);
        setAvailableCurrencies(['USD', 'KES', ...currencies.filter(c => c !== 'USD' && c !== 'KES')]);

        setDashboardData(processedData);
    };

    const handleCurrencyChange = (event, newCurrency) => {
        if (newCurrency !== null) {
            setSelectedCurrency(newCurrency);
            fetchExchangeRates(newCurrency);
            toast.success(`Switched to ${newCurrency} view`);
        }
    };

    const formatCurrency = (amount, currency = null) => {
        if (!amount && amount !== 0) return '0';
        const symbol = currency ? CURRENCY_SYMBOLS[currency] || currency : CURRENCY_SYMBOLS[selectedCurrency] || selectedCurrency;
        return `${symbol} ${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const handleRefresh = () => {
        fetchDashboardData();
        toast.success('Dashboard refreshed');
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading || !dashboardData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading dashboard...</Typography>
            </Box>
        );
    }

    const data = dashboardData;

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 3,
                gap: 2
            }}>
                <Box>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        Executive Dashboard
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Comprehensive overview of your business performance
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                    {/* Currency Selector */}
                    <ToggleButtonGroup
                        value={selectedCurrency}
                        exclusive
                        onChange={handleCurrencyChange}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                px: 2,
                                py: 0.5,
                                borderColor: '#e0e0e0',
                                fontWeight: 'bold',
                                fontSize: '0.8rem',
                                '&.Mui-selected': {
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#1565c0',
                                    }
                                }
                            }
                        }}
                    >
                        {availableCurrencies.map((currency) => (
                            <ToggleButton key={currency} value={currency}>
                                <CurrencyExchange sx={{ mr: 0.5, fontSize: 16 }} />
                                {currency}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    {ratesError ? (
                        <Chip icon={<WifiOff />} label="Offline" color="warning" size="small" />
                    ) : (
                        <Chip icon={<CloudDone />} label="Live" color="success" size="small" />
                    )}

                    <Tooltip title="Refresh Dashboard">
                        <IconButton onClick={handleRefresh} sx={{ color: '#1976d2' }} size={isMobile ? 'small' : 'medium'}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {/* Sync Status Bar */}
            {data.syncStatus && data.syncStatus.pending > 0 && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Box>
                            <strong>{data.syncStatus.pending}</strong> M-Pesa transactions pending sync
                            {data.syncStatus.total && ` (${data.syncStatus.synced} already synced)`}
                        </Box>
                        <Chip
                            label="Sync Now"
                            color="primary"
                            size="small"
                            onClick={() => window.location.href = '/admin/mpesa'}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Box>
                </Alert>
            )}

            {/* Currency Display Info */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                All values displayed in <strong>{selectedCurrency}</strong>
            </Typography>

            {/* KPI Cards */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={6} md={3}>
                    <Zoom in={true}>
                        <Card sx={{
                            height: '100%',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Clients</Typography>
                                        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold' }}>
                                            {data.totalClients}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            {data.totalTransactions} transactions
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                                        <People />
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Zoom>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Zoom in={true}>
                        <Card sx={{
                            height: '100%',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Revenue</Typography>
                                        <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '2rem' }}>
                                            {formatCurrency(data.totalRevenue)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            {Object.keys(data.currencyBreakdown).length} currencies
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                                        <AttachMoney />
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Zoom>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Zoom in={true}>
                        <Card sx={{
                            height: '100%',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            color: 'white',
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Expenses</Typography>
                                        <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '2rem' }}>
                                            {formatCurrency(data.totalExpenses)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            {data.expenseCount} transactions • {data.linkedExpenses} linked
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                                        <Receipt />
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Zoom>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Zoom in={true}>
                        <Card sx={{
                            height: '100%',
                            borderRadius: 2,
                            background: data.totalProfit >= 0
                                ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
                                : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Net Profit</Typography>
                                        <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '2rem' }}>
                                            {formatCurrency(data.totalProfit)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            Margin: {data.profitMargin.toFixed(1)}%
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                                        {data.totalProfit >= 0 ? <TrendingUp /> : <TrendingDown />}
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Zoom>
                </Grid>
            </Grid>

            {/* Status Cards */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
                <Grid item xs={4} sm={4} md={2.4}>
                    <Card sx={{ textAlign: 'center', p: 1.5, borderTop: `3px solid #4CAF50` }}>
                        <Typography variant="caption" color="text.secondary">Completed</Typography>
                        <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                            {data.completedCount}
                        </Typography>
                        <Chip size="small" label={`${data.totalClients > 0 ? ((data.completedCount / data.totalClients) * 100).toFixed(0) : 0}%`} color="success" />
                    </Card>
                </Grid>
                <Grid item xs={4} sm={4} md={2.4}>
                    <Card sx={{ textAlign: 'center', p: 1.5, borderTop: `3px solid #FF9800` }}>
                        <Typography variant="caption" color="text.secondary">Pending</Typography>
                        <Typography variant="h6" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
                            {data.pendingCount}
                        </Typography>
                        <Chip size="small" label={`${data.totalClients > 0 ? ((data.pendingCount / data.totalClients) * 100).toFixed(0) : 0}%`} color="warning" />
                    </Card>
                </Grid>
                <Grid item xs={4} sm={4} md={2.4}>
                    <Card sx={{ textAlign: 'center', p: 1.5, borderTop: `3px solid #F44336` }}>
                        <Typography variant="caption" color="text.secondary">Failed</Typography>
                        <Typography variant="h6" sx={{ color: '#F44336', fontWeight: 'bold' }}>
                            {data.failedCount}
                        </Typography>
                        <Chip size="small" label={`${data.totalClients > 0 ? ((data.failedCount / data.totalClients) * 100).toFixed(0) : 0}%`} color="error" />
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                    <Card sx={{ textAlign: 'center', p: 1.5, borderTop: `3px solid #1976d2` }}>
                        <Typography variant="caption" color="text.secondary">Services</Typography>
                        <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                            {Object.keys(data.serviceCount).length}
                        </Typography>
                        <Chip size="small" label="Active" color="primary" />
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                    <Card sx={{ textAlign: 'center', p: 1.5, borderTop: `3px solid #9C27B0` }}>
                        <Typography variant="caption" color="text.secondary">Packages</Typography>
                        <Typography variant="h6" sx={{ color: '#9C27B0', fontWeight: 'bold' }}>
                            {Object.keys(data.packageCount).length}
                        </Typography>
                        <Chip size="small" label="Available" color="secondary" />
                    </Card>
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Revenue Trend (in {selectedCurrency})
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={data.monthlyTrend}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                                <Area type="monotone" dataKey="revenue" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} name={`Revenue (${selectedCurrency})`} />
                                <Line type="monotone" dataKey="count" stroke="#82ca9d" name="Transactions" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Top Services
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                layout="vertical"
                                data={Object.entries(data.serviceCount)
                                    .sort((a, b) => b[1] - a[1])
                                    .slice(0, 8)
                                    .map(([name, value]) => ({ name, value }))}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis type="category" dataKey="name" width={80} />
                                <RechartsTooltip />
                                <Legend />
                                <Bar dataKey="value" fill="#8884d8" name="Bookings">
                                    {Object.entries(data.serviceCount).slice(0, 8).map((_, index) => (
                                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Top Clients - Now with proper currency conversion */}
            <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Top 5 Clients by Revenue (in {selectedCurrency})</Typography>
                    <Chip
                        label={`Total: ${formatCurrency(data.totalRevenue)}`}
                        sx={{ backgroundColor: '#1976d2', color: 'white' }}
                        size="small"
                    />
                </Box>
                <TableContainer>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                <TableCell>#</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell align="right">Revenue ({selectedCurrency})</TableCell>
                                <TableCell align="center">Transactions</TableCell>
                                <TableCell align="center">Original Currency</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.topClients.map((client, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>
                                        <Chip label={`#${index + 1}`} size="small" sx={{ bgcolor: COLORS[index % COLORS.length], color: 'white' }} />
                                    </TableCell>
                                    <TableCell>{client.name}</TableCell>
                                    <TableCell>{client.company}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                        {formatCurrency(client.revenue)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label={client.count} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label={client.original_currency || 'KES'} size="small" variant="outlined" color="secondary" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    * All revenues converted to {selectedCurrency} using live exchange rates
                </Typography>
            </Paper>

            {/* Quick Stats Footer */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <Card sx={{ bgcolor: '#e3f2fd' }}>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary">Currency Breakdown</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                {Object.entries(data.currencyBreakdown).map(([currency, stats]) => (
                                    <Chip
                                        key={currency}
                                        label={`${currency}: ${formatCurrency(stats.converted_total, selectedCurrency)} (${stats.count})`}
                                        sx={{
                                            bgcolor: currency === 'USD' ? '#1976d2' :
                                                currency === 'KES' ? '#4caf50' :
                                                    '#888',
                                            color: 'white'
                                        }}
                                        size="small"
                                    />
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Card sx={{ bgcolor: '#e8f5e9' }}>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary">Package Distribution</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                {Object.entries(data.packageCount).length > 0 ? (
                                    Object.entries(data.packageCount).map(([pkg, count]) => (
                                        <Chip key={pkg} label={`${pkg}: ${count}`} color="secondary" size="small" />
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No packages yet</Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}